/**
 * "Nowa seria" — the T-A4 canon's window: the series on the left (client, kind, start, length,
 * weekdays with their hours, weeks), every occurrence it makes on the right, and the one primary,
 * "Utwórz serię".
 *
 * The plan is this window's own state, seeded from the session being composed; "Jednorazowa" hands
 * what it shares with a single session (client, kind, length) back to the column. Occurrences, what
 * each collides with and the request are `model/recurringSeries`'. The calendar under the series is
 * read over its whole span (`trainerScheduleQueryOptions` over `seriesWindow`), so a conflict eight
 * weeks out is named before anything is sent.
 *
 * A conflict is FRAMED, never filled — the canon's amber frame on the row, and the words
 * "koliduje: …" — and never blocks (R9): "Przesuń" on the row opens the mover in the right column,
 * where that one occurrence gets another day or hour. After creation the server's own conflict
 * report is said once more (`useSessionWrites.seriesCreated`).
 *
 * An occurrence moved onto another of the same series collides too, and is framed the same way.
 * The check reads the calendar under the whole series and keeps its previous answer while a new
 * span is read (`keepPreviousData`), so editing never blinks the list; the banner keeps its box.
 *
 * "Jednorazowa" / "Powtarzalna" are two pressed-state buttons, never radios an arrow key would
 * switch: going to "Jednorazowa" hands the plan to the column, which keeps it for the way back.
 *
 * Base UI's dialog supplies the modality: focus is trapped and lands on the first field, Escape and
 * the cross close it — except while it is sending, when closing would orphan the answer — and focus
 * goes back to "Powtarzaj…" in the column.
 */

import { Dialog } from '@base-ui/react/dialog';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import ember from '~/shared/ui/emberButton.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import { FormField } from '~/shared/ui/FormField';
import seg from '~/shared/ui/segmented.module.css';
import split from '~/shared/ui/splitDialog.module.css';
import { WarningGlyph } from '~/shared/ui/WarningGlyph';

import { trainerScheduleQueryOptions } from '../api/bookingQueries';
import { readBookingSession } from '../model/bookingSession';
import { dayMonth, shortWeekday } from '../model/calendarWords';
import {
  collides,
  seriesNamesClient,
  seriesOccurrences,
  seriesProblem,
  seriesWindow,
  retimeWeekday,
  toggleWeekday,
  withWeeks,
  WEEK_BOUNDS,
  type ClientSeries,
  type SeriesOccurrence,
  type SeriesPlan,
} from '../model/recurringSeries';
import { DURATION_BOUNDS, withKind, type DraftPolicy } from '../model/sessionDraft';
import { clockOf, minutesOfTime, paddedClock, type CivilDay } from '../model/trainerClock';
import { ClientField } from './ClientField';
import compose from './ComposeForm.module.css';
import { KindChoice } from './KindChoice';
import { OccurrenceMover } from './OccurrenceMover';
import styles from './SeriesDialog.module.css';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

const DAY_MINUTES = 1440;

interface SeriesDialogProps {
  readonly seed: SeriesPlan;
  readonly zone: string;
  readonly policy: DraftPolicy;
  readonly nowMs: number;
  readonly saving: boolean;
  /** Why the server refused the last attempt, or `null`. */
  readonly refusal: string | null;
  readonly onOnce: (plan: SeriesPlan) => void;
  /** Closing keeps the plan — "Powtarzaj…" reopens it as it was left. */
  readonly onClose: (plan: SeriesPlan) => void;
  readonly onSubmit: (plan: ClientSeries, occurrences: readonly SeriesOccurrence[]) => void;
}

export function SeriesDialog({
  seed,
  zone,
  policy,
  nowMs,
  saving,
  refusal,
  onOnce,
  onClose,
  onSubmit,
}: SeriesDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const reasonId = useId();
  const formColumn = useRef<HTMLDivElement>(null);
  const liveColumn = useRef<HTMLDivElement>(null);
  const [plan, setPlan] = useState(seed);
  const [moving, setMoving] = useState<CivilDay | null>(null);
  const seedHour = [...seed.days.values()][0] ?? 0;

  const calendar = useQuery({
    ...trainerScheduleQueryOptions(seriesWindow(plan)),
    placeholderData: keepPreviousData,
  });
  const sessions =
    calendar.data === undefined
      ? null
      : calendar.data.map((event) => readBookingSession(event, zone));
  const occurrences = seriesOccurrences(plan, zone, sessions);
  const conflicted = occurrences.filter(collides);
  const blocker = seriesProblem(plan, occurrences, nowMs, zone);
  const problem = blocker?.problem ?? null;
  const reason =
    blocker === null
      ? null
      : t(`web:booking.series.problem.${blocker.problem}`, { number: blocker.number });
  const startError = problem === 'pastStart' || problem === 'past' ? reason : null;
  const last = occurrences.at(-1);

  const submit = () => {
    if (saving) return;
    if (problem !== null || !seriesNamesClient(plan)) {
      // Refused: to the field in the way — the client search, the start, the length or a weekday.
      formColumn.current
        ?.querySelector<HTMLElement>(
          problem === 'noDays' || problem === 'midnight'
            ? '[aria-pressed]'
            : '[aria-invalid="true"], input[type="search"]',
        )
        ?.focus();
      return;
    }
    onSubmit(plan, occurrences);
  };

  const when = (day: CivilDay) => `${shortWeekday(day, locale)} ${dayMonth(day)}`;
  /** `17:00–18:00`; a session running past midnight reads `23:30–0:30`, never `24:30`. */
  const rangeOf = (minutes: number) =>
    `${clockOf(minutes)}–${clockOf((minutes + plan.durationMinutes) % DAY_MINUTES)}`;
  const movingOccurrence = occurrences.find((occurrence) => occurrence.key === moving) ?? null;

  /** Back from the mover to the list, on the row it was opened from. */
  const backToList = (key: CivilDay) => {
    flushSync(() => {
      setMoving(null);
    });
    liveColumn.current?.querySelector<HTMLElement>(`[data-move-key="${key}"]`)?.focus();
  };

  return (
    <Dialog.Root
      open
      onOpenChange={(open, details) => {
        // Never while sending: the answer would have nowhere to land.
        if (open || saving) return;
        // Escape inside the mover steps back to the list; it never throws the series away.
        if (moving !== null && details.reason === 'escape-key') {
          backToList(moving);
          return;
        }
        onClose(plan);
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={split.backdrop} />
        <Dialog.Popup
          className={`${String(split.popup)} ${String(styles.window)}`}
          aria-busy={saving}
          initialFocus={() =>
            formColumn.current?.querySelector<HTMLElement>('input, button') ?? true
          }
        >
          <div className={split.head}>
            <div className={styles.titles}>
              <span className={styles.eyebrow}>{t('web:booking.create.eyebrow')}</span>
              <Dialog.Title className={split.title}>
                {t('calendar.create.titleSeries')}
              </Dialog.Title>
            </div>
            <span className={split.spacer} />
            <div
              className={`${String(seg.seg)} ${String(styles.freq)}`}
              role="group"
              aria-label={t('web:booking.series.freq')}
            >
              <button
                type="button"
                className={seg.segOption}
                aria-pressed={false}
                aria-disabled={saving ? 'true' : undefined}
                onClick={() => {
                  if (!saving) onOnce(plan);
                }}
              >
                {t('calendar.create.freq.once')}
              </button>
              <button
                type="button"
                className={`${String(seg.segOption)} ${String(seg.segOptionOn)}`}
                aria-pressed
              >
                {t('calendar.create.freq.recurring')}
              </button>
            </div>
            <Dialog.Close
              className={split.close}
              aria-label={t('common.close')}
              aria-disabled={saving ? 'true' : undefined}
            >
              <CloseGlyph />
            </Dialog.Close>
          </div>

          <div ref={formColumn} className={split.form}>
            <ClientField
              clientId={plan.clientId}
              onChoose={(clientId) => {
                setPlan({ ...plan, clientId });
              }}
            />
            <KindChoice
              kind={plan.kind}
              policy={policy}
              onChange={(kind) => {
                setPlan(withKind(plan, kind, policy));
              }}
            />
            <div className={styles.startRow}>
              <FormField
                label={t('calendar.create.startLabel')}
                type="date"
                value={plan.startDay}
                error={startError}
                onChange={(value) => {
                  if (value !== '') setPlan({ ...plan, startDay: value });
                }}
              />
              <FormField
                label={t('calendar.create.durationLabel')}
                type="number"
                inputMode="numeric"
                step={5}
                min={String(DURATION_BOUNDS.min)}
                max={String(DURATION_BOUNDS.max)}
                value={String(plan.durationMinutes)}
                error={problem === 'duration' ? reason : null}
                onChange={(value) => {
                  const minutes = Number(value);
                  if (value !== '' && Number.isFinite(minutes))
                    setPlan({ ...plan, durationMinutes: minutes });
                }}
                trailing={<span className={compose.unit}>{t('web:booking.create.minutes')}</span>}
              />
            </div>
            <fieldset className={compose.fieldset}>
              <legend className={compose.label}>{t('calendar.create.weekdaysLabel')}</legend>
              <div className={styles.weekdays}>
                {WEEKDAYS.map((weekday) => {
                  const minutes = plan.days.get(weekday);
                  const name = t(`web:weekdays.short.${String(weekday)}`);
                  return (
                    <div key={weekday} className={styles.weekday}>
                      <button
                        type="button"
                        className={`${String(styles.bubble)} ${minutes === undefined ? '' : String(styles.bubbleOn)}`}
                        aria-pressed={minutes !== undefined}
                        onClick={() => {
                          // A day added takes the hour the series started from.
                          setPlan(toggleWeekday(plan, weekday, seedHour));
                        }}
                      >
                        {name}
                      </button>
                      {minutes === undefined ? null : (
                        <input
                          className={styles.bubbleTime}
                          type="time"
                          step={900}
                          value={paddedClock(minutes)}
                          aria-label={t('web:booking.series.weekdayTime', { weekday: name })}
                          onChange={(event) => {
                            const next = minutesOfTime(event.target.value);
                            if (next !== null) setPlan(retimeWeekday(plan, weekday, next));
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <p className={styles.hint}>{t('web:booking.series.weekdayHint')}</p>
            </fieldset>

            <fieldset className={compose.fieldset}>
              <legend className={compose.label}>{t('calendar.create.weeksLabel')}</legend>
              <div className={styles.stepper}>
                <button
                  type="button"
                  className={styles.step}
                  aria-label={t('calendar.create.weeksDecrement')}
                  aria-disabled={plan.weeks <= WEEK_BOUNDS.min ? 'true' : undefined}
                  onClick={() => {
                    setPlan(withWeeks(plan, plan.weeks - 1));
                  }}
                >
                  −
                </button>
                <output className={styles.weeks} aria-live="polite">
                  <span className={styles.weeksFigure}>{plan.weeks}</span>
                  <span className={styles.weeksUnit}>{t('web:booking.series.weeksUnit')}</span>
                </output>
                <button
                  type="button"
                  className={styles.step}
                  aria-label={t('calendar.create.weeksIncrement')}
                  aria-disabled={plan.weeks >= WEEK_BOUNDS.max ? 'true' : undefined}
                  onClick={() => {
                    setPlan(withWeeks(plan, plan.weeks + 1));
                  }}
                >
                  +
                </button>
              </div>
              <p className={styles.hint}>{t('web:booking.series.weeksHint')}</p>
            </fieldset>

            <p className={compose.note}>{t('web:booking.series.stationary')}</p>
          </div>

          <div ref={liveColumn} className={split.live}>
            {movingOccurrence === null ? (
              <>
                <div className={styles.liveHead}>
                  <h3 className={styles.liveTitle}>{t('calendar.create.generatedLabel')}</h3>
                  {last === undefined ? null : (
                    <span className={styles.until}>
                      {t('web:booking.series.until', { date: when(last.day) })}
                    </span>
                  )}
                </div>

                {/* One box across checking, conflicts and none, so an answer never moves the list. */}
                <div className={styles.bannerBox}>
                  {sessions === null ? (
                    <p className={compose.note} role="status">
                      {calendar.isError
                        ? t('web:booking.series.unchecked')
                        : t('web:booking.series.checking')}
                    </p>
                  ) : conflicted.length > 0 ? (
                    <div className={styles.conflictBanner} role="status">
                      <WarningGlyph className={compose.noteGlyph} />
                      <span>
                        <strong>
                          {t('calendar.create.banner.seriesConflictTitle', {
                            count: conflicted.length,
                            total: occurrences.length,
                          })}
                        </strong>
                        <br />
                        {t('calendar.create.banner.seriesConflictMessage')}
                      </span>
                    </div>
                  ) : occurrences.length > 0 ? (
                    <p className={compose.note} role="status">
                      {t('web:booking.series.noConflicts')}
                    </p>
                  ) : null}
                </div>

                <ol className={styles.occurrences}>
                  {occurrences.map((occurrence) => {
                    const clash = occurrence.conflicts?.[0] ?? null;
                    const sibling = occurrence.siblings[0] ?? null;
                    const collision =
                      clash !== null
                        ? t('web:booking.series.conflict', {
                            name: clash.clientName ?? t('web:booking.unnamedClient'),
                          })
                        : sibling !== null
                          ? t('web:booking.series.siblingConflict', { number: sibling })
                          : null;
                    const range = rangeOf(occurrence.minutes);
                    return (
                      <li
                        key={occurrence.key}
                        className={`${String(styles.occurrence)} ${collision === null ? '' : String(styles.occurrenceClash)}`}
                      >
                        <span className={styles.number}>{occurrence.number}</span>
                        <span className={styles.day}>{when(occurrence.day)}</span>
                        <span className={styles.range}>{range}</span>
                        <span className={styles.clash}>
                          {collision ?? (occurrence.moved ? t('web:booking.series.moved') : '')}
                        </span>
                        <button
                          type="button"
                          className={`${String(styles.move)} ${collision === null ? '' : String(styles.moveClash)}`}
                          data-move-key={occurrence.key}
                          aria-label={t('web:booking.series.moveLabel', {
                            number: occurrence.number,
                            when: `${when(occurrence.day)} ${range}`,
                          })}
                          onClick={() => {
                            setMoving(occurrence.key);
                          }}
                        >
                          {t('web:booking.series.move')}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </>
            ) : (
              <OccurrenceMover
                plan={plan}
                occurrence={movingOccurrence}
                total={occurrences.length}
                zone={zone}
                nowMs={nowMs}
                onMove={setPlan}
                onDone={() => {
                  backToList(movingOccurrence.key);
                }}
              />
            )}
          </div>

          <div className={split.foot}>
            <span className={styles.equation}>
              <span className={styles.term}>
                {t('web:booking.series.perWeek', { count: plan.days.size })}
              </span>
              <span aria-hidden="true">×</span>
              <span className={styles.term}>
                {t('web:booking.series.weeks', { count: plan.weeks })}
              </span>
              <span aria-hidden="true">=</span>
              <strong className={styles.total}>
                {t('web:booking.series.total', { count: occurrences.length })}
              </strong>
            </span>
            {refusal === null ? null : (
              <span className={split.failure} role="alert">
                {refusal}
              </span>
            )}
            <span className={split.spacer} />
            {reason === null ? null : (
              <span id={reasonId} className={split.reason}>
                {reason}
              </span>
            )}
            <Dialog.Close className={split.ghost} aria-disabled={saving ? 'true' : undefined}>
              {t('web:booking.move.cancel')}
            </Dialog.Close>
            <button
              type="button"
              className={`${String(ember.ember)} ${String(split.primary)}`}
              aria-disabled={problem !== null || saving ? 'true' : undefined}
              aria-describedby={reason === null ? undefined : reasonId}
              onClick={submit}
            >
              {saving ? t('web:booking.series.saving') : t('calendar.create.ctaSeries')}
            </button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
